import React, { useState, useEffect } from 'react';
import { Flex, Card, CardHeader, CardBody, Text } from '@chakra-ui/react';
import ReskinnedBenchmarkOWStat from './ReskinnedBenchmarkOWStat';
import CustomDateRangePicker from '../../utility/dateRangePicker';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import './reskinnedBenchmarkOverview.css';
import CustomLocationSelector from '../../utility/customLocationSelector';

/**
 * Reskinned Benchmark Overview component that matches the Figma design
 * This component is used when the showReskin feature flag is enabled
 */
const ReskinnedBenchmarkOverview = props => {
  const [overview, setOverview] = useState({});

  const handleDate = value => {
    const fromDate =
      (value[0].getMonth() > 8
        ? value[0].getMonth() + 1
        : '0' + (value[0].getMonth() + 1)) +
      '-' +
      (value[0].getDate() > 9 ? value[0].getDate() : '0' + value[0].getDate()) +
      '-' +
      value[0].getFullYear();

    const toDate =
      (value[1].getMonth() > 8
        ? value[1].getMonth() + 1
        : '0' + (value[1].getMonth() + 1)) +
      '-' +
      (value[1].getDate() > 9 ? value[1].getDate() : '0' + value[1].getDate()) +
      '-' +
      value[1].getFullYear();

    const formData = new FormData();
    formData.append('fromDate', fromDate);
    formData.append('toDate', toDate);

    fetchWithAuth('/api/benchmark_data/', {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          setOverview(data['overview']);
        } else {
          alert('Session Expired!.');
          window.open('/');
        }
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    fetchWithAuth('/api/benchmark_data/', {
      method: 'POST',
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          setOverview(data['overview']);
        } else {
          window.open('/', '_self');
          alert('Session Expired!.');
        }
      })
      .catch(err => {
        console.error(err);
      });
  }, []);

  // Calculate performance comparison values
  const costComparison =
    props.overview?.avg_cost && props.overview?.exp_rev
      ? (
          parseFloat(props.overview.avg_cost) -
          parseFloat(props.overview.exp_rev)
        ).toFixed(2) + '%'
      : '-';

  const incomeComparison =
    props.overview?.avg_inc && props.overview?.inc_rev
      ? (
          parseFloat(props.overview.inc_rev) -
          parseFloat(props.overview.avg_inc)
        ).toFixed(2) + '%'
      : '-';

  // Determine performance status for cost (lower is better)
  const getPerformanceStatusCost = () => {
    // If we don't have data or avg_cost is 0, return neutral
    if (
      !props.overview?.avg_cost ||
      !props.overview?.exp_rev ||
      parseFloat(props.overview.avg_cost) === 0
    )
      return 'neutral';

    const expRev = parseFloat(props.overview.exp_rev);
    const avgCost = parseFloat(props.overview.avg_cost);

    // Calculate the difference as a percentage of the average cost
    const diff = Math.abs(expRev - avgCost) / avgCost;

    // If the difference is less than 1%, consider it neutral
    if (diff < 0.01) return 'neutral';

    // Otherwise, if cost is lower than average, it's positive
    return expRev < avgCost ? 'positive' : 'negative';
  };

  // Determine performance status for income (higher is better)
  const getPerformanceStatusIncome = () => {
    // If we don't have data or avg_inc is 0, return neutral
    if (
      !props.overview?.avg_inc ||
      !props.overview?.inc_rev ||
      parseFloat(props.overview.avg_inc) === 0
    )
      return 'neutral';

    const incRev = parseFloat(props.overview.inc_rev);
    const avgInc = parseFloat(props.overview.avg_inc);

    // Calculate the difference as a percentage of the average income
    const diff = Math.abs(incRev - avgInc) / avgInc;

    // If the difference is less than 1%, consider it neutral
    if (diff < 0.01) return 'neutral';

    // Otherwise, if income is higher than average, it's positive
    return incRev > avgInc ? 'positive' : 'negative';
  };

  const isOutperformingCost = getPerformanceStatusCost();
  const isOutperformingIncome = getPerformanceStatusIncome();

  return (
    <Card width={'100%'} className="benchmark-overview-card">
      <CardHeader>
        <Flex style={{ marginBottom: '2rem' }}>
          <Flex gap={2} flex={1} alignItems={'center'} width={'100%'}>
            <Text
              color="var(--Text-neutral-strongest, #0A091B)"
              fontFamily="var(--font-family-sans, Poppins)"
              fontSize="var(--font-size-600, 2rem)"
              fontStyle="normal"
              fontWeight={600}
              lineHeight="var(--font-line-height-600, 2.5rem)"
            >
              Overview
            </Text>
          </Flex>
          <Flex flex={1} justifyContent={'flex-end'}>
            <Flex direction={{ base: 'column', md: 'row' }} gap="16px">
              <Flex maxWidth={{ base: '100%', md: '250px' }}>
                <CustomLocationSelector
                  locationValue={props.locationValue}
                  setLocation={props.setLocation}
                />
              </Flex>

              <Flex width={{ base: '100%', md: 'auto' }}>
                <CustomDateRangePicker
                  dateValue={props.handleDate}
                  value={props.value}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody overflowX={'auto'}>
        <Flex direction={{ base: 'column', lg: 'row' }} gap="24px" width="100%">
          {/* Cost Card */}
          <Flex flex="1">
            <ReskinnedBenchmarkOWStat
              header="Your Cost"
              num={
                props.overview !== undefined
                  ? props.overview.exp_rev + '%'
                  : '-'
              }
              denom={
                props.overview !== undefined
                  ? props.overview.inc_rev + '%'
                  : '-'
              }
              numDesc="Cost"
              denomDesc="Net Income"
              avgCost={
                props.overview !== undefined
                  ? props.overview.avg_cost + '%'
                  : '-'
              }
              avgInc={
                props.overview !== undefined
                  ? props.overview.avg_inc + '%'
                  : '-'
              }
              bicCost={
                props.overview !== undefined
                  ? props.overview.bic_cost + '%'
                  : '-'
              }
              bicInc={
                props.overview !== undefined
                  ? props.overview.bic_inc + '%'
                  : '-'
              }
              comparisonValue={costComparison}
              isOutperforming={isOutperformingCost}
            />
          </Flex>

          {/* Income Card */}
          <Flex flex="1">
            <ReskinnedBenchmarkOWStat
              header="Your Net Income"
              num={
                props.overview !== undefined
                  ? props.overview.inc_rev + '%'
                  : '-'
              }
              denom={
                props.overview !== undefined
                  ? props.overview.exp_rev + '%'
                  : '-'
              }
              numDesc="Net Income"
              denomDesc="Cost"
              avgCost={
                props.overview !== undefined
                  ? props.overview.avg_cost + '%'
                  : '-'
              }
              avgInc={
                props.overview !== undefined
                  ? props.overview.avg_inc + '%'
                  : '-'
              }
              bicCost={
                props.overview !== undefined
                  ? props.overview.bic_cost + '%'
                  : '-'
              }
              bicInc={
                props.overview !== undefined
                  ? props.overview.bic_inc + '%'
                  : '-'
              }
              comparisonValue={incomeComparison}
              isOutperforming={isOutperformingIncome}
            />
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default ReskinnedBenchmarkOverview;
